import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { User, authApi, LoginData, RegisterData } from '../utils/api';

interface AuthContextType {
  user: User | null;
  loading: boolean;
  login: (loginData: LoginData) => Promise<void>;
  register: (registerData: RegisterData) => Promise<void>;
  logout: () => Promise<void>;
  updateUser: (updatedUser: User) => void;
  isAuthenticated: boolean;
  isProfileComplete: boolean;
  needsProfileSetup: boolean;
  clearProfileSetup: () => void;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

interface AuthProviderProps {
  children: ReactNode;
}

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);
  const [needsProfileSetup, setNeedsProfileSetup] = useState(false);

  // Check if user is logged in on app start
  useEffect(() => {
    const checkAuth = async () => {
      try {
        const token = localStorage.getItem('access_token');
        if (token) {
          const currentUser = await authApi.getCurrentUser();
          setUser(currentUser);
        }
      } catch (error) {
        console.error('Auth check failed:', error);
        localStorage.removeItem('access_token');
      } finally {
        setLoading(false);
      }
    };

    checkAuth();
  }, []);

  const login = async (loginData: LoginData) => {
    try {
      const authResponse = await authApi.login(loginData);
      localStorage.setItem('access_token', authResponse.access_token);
      
      // Set user info from auth response (shua_backend returns user info in login response)
      setUser({
        id: authResponse.user_id,
        email: authResponse.email,
        display_name: authResponse.display_name,
        profile_picture: null // Will be loaded from getCurrentUser if available
      });
    } catch (error) {
      console.error('Login failed:', error);
      throw error;
    }
  };

  const register = async (registerData: RegisterData): Promise<void> => {
    try {
      const authResponse = await authApi.register(registerData);
      
      // shua_backend returns auth info directly from register, so set user and token
      localStorage.setItem('access_token', authResponse.access_token);
      setNeedsProfileSetup(true);
      setUser({
        id: authResponse.user_id,
        email: authResponse.email,
        display_name: authResponse.display_name,
        profile_picture: null
      });
    } catch (error) {
      console.error('Registration failed:', error);
      throw error;
    }
  };

  const clearProfileSetup = () => {
    setNeedsProfileSetup(false);
  };

  const logout = async () => {
    try {
      await authApi.logout();
    } catch (error) {
      console.error('Logout error:', error);
    } finally {
      setUser(null);
      localStorage.removeItem('access_token');
    }
  };

  const updateUser = (updatedUser: User) => {
    // Create a new object reference to ensure React detects the change
    setUser({ ...updatedUser });
  };

  const isProfileComplete = !!user && Array.isArray(user.sports) && user.sports.length > 0;

  const value: AuthContextType = {
    user,
    loading,
    login,
    register,
    logout,
    updateUser,
    isAuthenticated: !!user,
    isProfileComplete,
    needsProfileSetup,
    clearProfileSetup,
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};